/*
 * Centralized default configuration for the Interplant Movement Tracking
 * Dashboard screen. Labels, tab definitions, filter definitions and grid
 * columns all live here so the screen component never hardcodes them.
 *
 * The shape mirrors the "dashboard" config object served through the generic
 * POST /config/fetchServiceConfig endpoint (config_type "app", service_name
 * "interplant_movement_tracking_dashboard", source "ui") — see
 * hero_node/routes/controller/services/serviceConfig/fetchServiceConfig.js
 * and hero_node/routes/controller/services/interplantmovementtracking/seedInterplantMovementTrackingConfig.js.
 */
export const DEFAULT_CONFIG = {
	dashboard: {
		title: "Interplant Movement Tracking Dashboard",
		subtitle: "Track interplant movements and monitor transit performance",
		totalRecordsLabel: "Total Records",
		tabs: [
			{ id: "CHALLAN", label: "Challan Details", whp_trip_type_identifier: "CHALLAN" },
			{ id: "STO", label: "STO Details", whp_trip_type_identifier: "STO" }
		],
		defaultTab: "CHALLAN",
		filters: {
			consigner: { enabled: true, label: "Consigner", valueField: "plant_code", labelField: "plant_name" },
			fromDate: { enabled: true, label: "From Date" },
			toDate: { enabled: true, label: "To Date" }
		},
		// Default From/To Date range applied on load and on "Reset" — config-driven so it can be
		// changed without a code change. mode: "none" | "today" | "lastNDays" (uses `days`) |
		// "monthToDate" | "lastNMonths" (uses `months`, true calendar-month subtraction).
		defaultDateRange: {
			mode: "lastNMonths",
			months: 1
		},
		// Raw numeric `status` code -> display label, used by getStatusLabel() in
		// interplantmovementtracking.js for the Status column in the detail drawer grid.
		// Matches the canonical status mapping used app-wide (see consignments.js's Status
		// column valueGetter) so labels stay consistent across screens. Config-driven so the
		// wording can change (or new codes be added) without a code change.
		statusLabels: {
			1: "Inside Plant",
			2: "In Transit",
			3: "Near Destination",
			4: "Reached Destination",
			5: "Left Destination",
			6: "Force Closed",
			7: "Proximity Trip Closure"
		},
		// Detail drawer (slide-out panel) width, as a viewport-width percentage. `widthPercent`
		// is the desktop width; `tabletWidthPercent`/`mobileWidthPercent` apply at the existing
		// ≤1200px/≤768px breakpoints. Config-driven so it can change without a code change.
		drawer: {
			widthPercent: 60,
			tabletWidthPercent: 75,
			mobileWidthPercent: 100
		},
		// Screen-wide DEFAULT moment.js format for every column whose config sets
		// cellRenderer:"dateFormater" (main table + detail drawer grid) — see IpmtDateFormater /
		// mapColumnDef() in interplantmovementtracking.js. Defaults to the exact format the shared
		// dateComponent.js renderer has always used, so leaving this out of a config document
		// keeps prior behavior unchanged.
		//
		// Two-stage/tunable via config: this is the global fallback. Any individual column below
		// (table.columns / statusSummary.baseDetailColumns / a group's detailColumns) can instead
		// set its OWN `dateFormat` field right next to its `cellRenderer`, e.g.
		// `{ field: "invoice_time", headerName: "Invoice Time", width: 160, cellRenderer: "dateFormater", dateFormat: "DD-MM-YYYY" }`
		// — that per-column value wins over this global default for that one column only. No
		// column below currently sets its own `dateFormat`, so today every date column still
		// resolves to this single screen-wide value; add it to a specific column's config entry
		// whenever that one column needs a different format than the rest.
		dateFormat: "DD-MM-YYYY HH:mm:ss",
		table: {
			enabled: false,
			loadingLabel: "Loading interplant movement data...",
			emptyStateLabel: "No records found",
			errorLabel: "Unable to load data. Please try again.",
			columns: [
				{ field: "plant_code", headerName: "Plant Code", width: 120 },
				{ field: "plant_name", headerName: "Plant Name", width: 160 },
				{ field: "consignee_code", headerName: "Consignee Code", width: 140 },
				{ field: "consignee_name", headerName: "Consignee Name", width: 180 },
				{ field: "consignee_city", headerName: "Consignee City", width: 140 },
				{ field: "consignee_state", headerName: "Consignee State", width: 140 },
				{ field: "zone", headerName: "Zone", width: 100 },
				{ field: "distance_in_km", headerName: "Distance (KM)", width: 130 },
				{ field: "near_customer_distance_km", headerName: "Near Customer Distance (KM)", width: 190 },
				{ field: "transit_time", headerName: "Transit Time (Hrs)", width: 150 },
				{ field: "updated_at", headerName: "Updated At", width: 160, cellRenderer: "dateFormater" }
			]
		},
		statusSummary: {
			enabled: true,
			breakdownLabelField: "consignee_name",
			breakdownValueField: "consignee_code",
			// Status codes fetched from `consignments` for the summary (and therefore for the
			// "Total Records" badge, which sums this same response) — sent as-is to
			// POST /consignments/interplantmovementstatussummary. Mirrors the backend's own
			// DEFAULT_INTERPLANT_ALLOWED_STATUSES fallback (consignmentController.js) so behavior
			// is identical whether or not this config document overrides it. Config-driven: change
			// this array (no code change, either side) to widen/narrow which statuses are fetched.
			// Dropped 1 (Inside Plant) — matches atDealerShipDashboard.js's own status semantics
			// (status == 2 is "In Transit"; a truck still inside the plant hasn't dispatched yet).
			allowedStatuses: [2, 3, 4, 5],
			// Range/No Of Days rule engine, applied server-side (applyInterplantRangeRules in
			// hero_node's consignmentController.js) to every detail-drawer row, keyed by that
			// row's own raw `status` — independent of which counter/group was clicked (each group
			// below now maps onto exactly one rangeRules entry: in_transit->[2], reached_waiting
			// ->[3,4], left_unloaded->[5]). No thresholds are hardcoded in hero_node; this array is
			// sent as-is in the request body (see
			// openStatusDetailSlider in interplantmovementtracking.js), the same way
			// allowedStatuses/groupByValueField already are for this screen.
			//
			// method "daysUntil": days = abs(field - now)/24h, field is a future-ish date
			//   (e.g. revised_trip_end — the truck's expected/revised arrival time).
			// method "daysSince": days = abs(now - field)/24h, field is a past date (e.g.
			//   recent_dealer_reported — last time the truck was reported near/at the dealer).
			// method "hoursField": field is already a plain hours number (e.g.
			//   unloading_time_in_hrs), not a date. displayUnit "days" (default) converts it to a
			//   day count for No Of Days; "hours" shows the raw hours instead — Range is always
			//   bucketed from the days-equivalent either way.
			// buckets: ordered [{maxDays, label}], first entry with days <= maxDays wins
			//   (maxDays: null is the catch-all and must stay last).
			//
			// Status 1 (Inside Plant) and 6 (Force Closed) intentionally have no rule — Range/No
			// Of Days render blank for those rows.
			rangeRules: [
				{
					statusValues: [2],
					method: "daysUntil",
					field: "revised_trip_end",
					buckets: [
						{ maxDays: 1, label: "0-1" },
						{ maxDays: 3, label: "2-3" },
						{ maxDays: null, label: "3+" }
					]
				},
				{
					statusValues: [3, 4],
					method: "daysSince",
					field: "recent_dealer_reported",
					buckets: [
						{ maxDays: 1, label: "0-1" },
						{ maxDays: 3, label: "2-3" },
						{ maxDays: null, label: "3+" }
					]
				},
				{
					statusValues: [5, 7],
					method: "hoursField",
					field: "unloading_time_in_hrs",
					displayUnit: "days",
					buckets: [
						{ maxDays: 1, label: "0-1" },
						{ maxDays: 3, label: "2-3" },
						{ maxDays: null, label: "3+" }
					]
				}
			],
			baseDetailColumns: [
				{ field: "consignment_code", headerName: "GR No", width: 150 },
				{ field: "truck_no", headerName: "Truck No", width: 130 },
				{ field: "consigner_code", headerName: "Consigner Code", width: 140 },
				{ field: "consignee_code", headerName: "Consignee Code", width: 140 },
				{ field: "consignee_name", headerName: "Consignee Name", width: 170 },
				{ field: "consignee_city", headerName: "Consignee City", width: 140 },
				{ field: "transporter_name", headerName: "Transporter", width: 180 },
				{ field: "invoice_time", headerName: "Invoice Time", width: 160, cellRenderer: "dateFormater" },
				// computed_range/computed_no_of_days/computed_distance_travelled are computed
				// server-side (applyInterplantRangeRules in hero_node's consignmentController.js,
				// driven by rangeRules above) and joined in (previousdaydistances, for distance) —
				// plain fields here, no special cellRenderer needed since the value arriving from
				// the API is already final.
				{ field: "computed_range", headerName: "Range", width: 120 },
				{ field: "computed_no_of_days", headerName: "No Of Days", width: 120 },
				{ field: "computed_distance_travelled", headerName: "Distance Travelled (Yesterday, KM)", width: 200 },
				{ field: "status", headerName: "Status", width: 100 }
			],
			groups: [
				{
					id: "in_transit",
					label: "Total In Transit Trucks",
					statusValues: [2],
					colorClass: "ipmt-kpi-green",
					icon: "truck",
					detailColumns: [
						{ field: "gate_out_time", headerName: "Dispatched On", width: 160, cellRenderer: "dateFormater" }
					]
				},
				{
					id: "reached_waiting",
					label: "Total Reached Destination / Waiting for Unloading Trucks",
					statusValues: [3, 4],
					colorClass: "ipmt-kpi-red",
					icon: "pin",
					detailColumns: [
						{ field: "gate_in_time", headerName: "Reached On", width: 160, cellRenderer: "dateFormater" },
						// Label matches atDealerShipDashboard.js's criticalColumns entry for this same
						// field exactly ("Recent Dealer Reported"), so it reads as the same column
						// across both screens.
						{ field: "recent_dealer_reported", headerName: "Recent Dealer Reported", width: 160, cellRenderer: "dateFormater" }
					]
				},
				{
					id: "left_unloaded",
					label: "Total left Destination / Unloaded Trucks",
					statusValues: [5],
					colorClass: "ipmt-kpi-yellow",
					icon: "truck",
					// atDealerShipDashboard.js's "Total Unloaded" view shows both Recent Dealer
					// Reported (last report before departure) and Left Dealer Date (when it left) --
					// mirrored here with the same two fields and the same labels.
					detailColumns: [
						{ field: "recent_dealer_reported", headerName: "Recent Dealer Reported", width: 160, cellRenderer: "dateFormater" },
						{ field: "left_dealer_marked", headerName: "Left Dealer Date", width: 170, cellRenderer: "dateFormater" }
					]
				}
			]
		}
	}
};

export default DEFAULT_CONFIG;
